"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { Button } from '@/components/ui/button'
import { Bell, X } from "lucide-react"
import { useSellerId } from "@/hooks/useSellerId"
import { createPortal } from "react-dom"
import { useRouter } from "next/navigation"

type NotificationItem = {
  id: string
  source: "direct" | "global"
  inboxId: number | null
  eventId: number
  sellerId: number
  key: string
  title: string
  href: string
  avatar?: string | null
  actorName: string
  read: boolean
  readAt: string | null
  createdAt: string
}

function formatNotificationTime(input: string | Date | null | undefined) {
  if (!input) return ""

  const date = input instanceof Date ? input : new Date(input)
  if (Number.isNaN(date.getTime())) return ""

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return "Justo ahora"
  if (diffMin < 60) return `Hace ${diffMin} ${diffMin === 1 ? "minuto" : "minutos"}`
  if (diffHour < 24) return `Hace ${diffHour} ${diffHour === 1 ? "hora" : "horas"}`
  if (diffDay === 1) return "Ayer"

  if (diffDay < 7) {
    const weekday = date.toLocaleDateString("es-MX", { weekday: "long" })
    return weekday.charAt(0).toUpperCase() + weekday.slice(1)
  }

  return date.toLocaleDateString("es-MX", {
    month: "short",
    day: "numeric",
  })
}

export function NotificationBell() {
  const { sellerId, loading: sellerLoading } = useSellerId()

  const [open, setOpen] = useState(false)
  const [items, setItems] = useState<NotificationItem[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const portalRef = useRef<HTMLDivElement | null>(null)
  const router = useRouter()

  const buttonRef = useRef<HTMLButtonElement | null>(null)
  const [mounted, setMounted] = useState(false)
  const [dropdownPos, setDropdownPos] = useState({
    top: 0,
    right: 16,
  })

  useEffect(() => {
    setMounted(true)
  }, [])

  const ref = useRef<HTMLDivElement | null>(null)

  function updateDropdownPosition() {
    const button = buttonRef.current
    if (!button) return

    const rect = button.getBoundingClientRect()

    setDropdownPos({
        top: rect.bottom + 12,
        right: Math.max(16, window.innerWidth - rect.right),
    })
  }

  async function refreshUnreadCount() {
    if (!sellerId) return

    try {
        const res = await fetch(`/api/notifications?sellerId=${sellerId}&limit=1`, {
        credentials: "include",
        cache: "no-store",
        })

        const json = await res.json().catch(() => ({}))

        if (res.ok && json?.ok) {
        setUnreadCount(Number(json.unreadCount || 0))
        }
    } catch {
        // silent
    }
  }

  async function loadNotifications() {
    if (!sellerId) return

    setLoading(true)

    try {
      const res = await fetch(`/api/notifications?sellerId=${sellerId}&limit=20`, {
        credentials: "include",
      })

      const json = await res.json().catch(() => ({}))

      if (res.ok && json?.ok) {
        setItems(json.items || [])
        setUnreadCount(Number(json.unreadCount || 0))
      }
    } finally {
      setLoading(false)
    }
  }

  async function markRead(item: NotificationItem) {
    if (item.read) return

    setItems((current) =>
      current.map((entry) =>
        entry.id === item.id ? { ...entry, read: true } : entry
      )
    )

    setUnreadCount((current) => Math.max(0, current - 1))

    await fetch("/api/notifications/read", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        sellerId,
        source: item.source,
        inboxId: item.inboxId,
        eventId: item.eventId,
      }),
    }).catch(() => {})
  }

  async function markAllRead() {
    if (!sellerId) return

    setItems((current) => current.map((item) => ({ ...item, read: true })))
    setUnreadCount(0)

    await fetch("/api/notifications/read-all", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ sellerId }),
    }).catch(() => {})
  }

  useEffect(() => {
    if (!sellerLoading && sellerId) {
      loadNotifications()
    }
  }, [sellerLoading, sellerId])

  useEffect(() => {
    if (sellerLoading || !sellerId) return

    refreshUnreadCount()

    const interval = window.setInterval(() => {
        refreshUnreadCount()
    }, 30000)

    return () => window.clearInterval(interval)
  }, [sellerLoading, sellerId])

  useEffect(() => {
    function onClickOutside(event: MouseEvent) {
      const target = event.target as Node

      const clickedButton = ref.current?.contains(target)
      const clickedPortal = portalRef.current?.contains(target)

      if (clickedButton || clickedPortal) return

      setOpen(false)
    }

    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [])

  useEffect(() => {
    if (!open) return

    function handleReposition() {
        updateDropdownPosition()
    }

    window.addEventListener("resize", handleReposition)
    window.addEventListener("scroll", handleReposition, true)

    return () => {
        window.removeEventListener("resize", handleReposition)
        window.removeEventListener("scroll", handleReposition, true)
    }
  }, [open])

  const notificationContent = (
    <>
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <div>
            <p className="text-sm font-semibold text-foreground">
            Notificaciones
            </p>
            <p className="text-xs text-muted-foreground">
            {unreadCount > 0 ? `${unreadCount} sin leer` : "Todo al día"}
            </p>
        </div>

        {unreadCount > 0 && (
            <button
            type="button"
            onClick={markAllRead}
            className="text-xs font-medium text-muted-foreground transition hover:text-foreground"
            >
            Marcar todas
            </button>
        )}
        </div>

        <div className="max-h-[430px] overflow-y-auto md:max-h-[430px]">
        {loading && items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            Cargando...
            </div>
        ) : items.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No tienes notificaciones todavía.
            </div>
        ) : (
            items.map((item) => (
            <button
                key={item.id}
                type="button"
                onClick={async () => {
                await markRead(item)
                setOpen(false)
                router.push(item.href)
                }}
                className={`flex w-full gap-3 border-b border-border px-4 py-3 text-left transition hover:bg-muted/60 ${
                  item.read
                    ? "bg-background"
                    : "bg-muted/40"
                }`}
            >
                <div className="mt-0.5 h-10 w-10 shrink-0 overflow-hidden rounded-full bg-muted">
                {item.avatar ? (
                    <img
                    src={item.avatar}
                    alt={item.actorName}
                    className="h-full w-full object-cover"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-muted-foreground">
                    {item.actorName?.charAt(0)?.toUpperCase() || "H"}
                    </div>
                )}
                </div>

                <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-snug text-foreground">
                    {item.title}
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                    {formatNotificationTime(item.createdAt)}
                </p>
                </div>

                {!item.read && (
                <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                )}
            </button>
            ))
        )}
        </div>
    </>
    )

  if (sellerLoading || !sellerId) return null

  return (
    <div ref={ref} className="relative ml-auto inline-flex">
        <Button
        ref={buttonRef}
        type="button"
        onClick={() => {
            const next = !open

            if (next) {
            updateDropdownPosition()
            loadNotifications()
            }

            setOpen(next)
        }}
        variant="ghost"
        size="icon"
        className="hubee-header-control relative !h-12 !w-12 !rounded-[16px] !p-0"
        title="Notificaciones"
        >
        <Bell className="h-5 w-5" />

        {unreadCount > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-semibold text-white">
            {unreadCount > 9 ? "9+" : unreadCount}
            </span>
        )}
        </Button>

        {mounted && open
        ? createPortal(
            <div ref={portalRef}>
                {/* Desktop dropdown */}
                <div
                className="
                  fixed
                  hidden
                  w-[380px]
                  overflow-hidden
                  rounded-2xl
                  border
                  border-border
                  bg-background
                  text-foreground
                  shadow-2xl
                  md:block
                "
                style={{
                    top: dropdownPos.top,
                    right: dropdownPos.right,
                    zIndex: 2147483647,
                }}
                >
                {notificationContent}
                </div>

                {/* Mobile drawer */}
                <div
                className="fixed inset-0 md:hidden"
                style={{ zIndex: 2147483647 }}
                >
                <button
                    type="button"
                    aria-label="Cerrar notificaciones"
                    className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                    onClick={() => setOpen(false)}
                />

                <div className="
                  absolute
                  right-0
                  top-0
                  flex
                  h-dvh
                  w-[88vw]
                  max-w-[380px]
                  flex-col
                  overflow-hidden
                  bg-background
                  text-foreground
                  shadow-2xl
                ">
                    <div className="flex items-center justify-between border-b border-border px-4 py-4">
                    <div>
                        <p className="text-base font-semibold text-foreground">
                        Notificaciones
                        </p>
                        <p className="text-xs text-muted-foreground">
                        {unreadCount > 0
                            ? `${unreadCount} sin leer`
                            : "Todo al día"}
                        </p>
                    </div>

                    <button
                        type="button"
                        onClick={() => setOpen(false)}
                        className="
                          inline-flex
                          h-9
                          w-9
                          items-center
                          justify-center
                          rounded-full
                          bg-muted
                          text-muted-foreground
                          transition
                          hover:bg-muted/80
                          hover:text-foreground
                        "
                        aria-label="Cerrar"
                    >
                        <X className="h-4 w-4" />
                    </button>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                    {loading && items.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                        Cargando...
                        </div>
                    ) : items.length === 0 ? (
                        <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                        No tienes notificaciones todavía.
                        </div>
                    ) : (
                        items.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            onClick={async () => {
                            await markRead(item)
                            setOpen(false)
                            router.push(item.href)
                            }}
                            className={`flex w-full gap-3 border-b border-border px-4 py-4 text-left transition active:bg-muted/70 ${
                              item.read
                                ? "bg-background"
                                : "bg-muted/40"
                            }`}
                        >
                            <div className="mt-0.5 h-11 w-11 shrink-0 overflow-hidden rounded-full bg-muted">
                            {item.avatar ? (
                                <img
                                src={item.avatar}
                                alt={item.actorName}
                                className="h-full w-full object-cover"
                                />
                            ) : (
                                <div className="flex h-full w-full items-center justify-center text-xs font-semibold text-muted-foreground">
                                {item.actorName?.charAt(0)?.toUpperCase() || "H"}
                                </div>
                            )}
                            </div>

                            <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium leading-snug text-foreground">
                                {item.title}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                                {formatNotificationTime(item.createdAt)}
                            </p>
                            </div>

                            {!item.read && (
                            <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                            )}
                        </button>
                        ))
                    )}
                    </div>

                    {unreadCount > 0 && (
                    <div className="border-t border-border p-4">
                        <button
                        type="button"
                        onClick={markAllRead}
                        className="
                          w-full
                          rounded-xl
                          bg-primary
                          px-4
                          py-3
                          text-sm
                          font-semibold
                          text-primary-foreground
                          transition
                          hover:bg-primary/90
                        "
                        >
                        Marcar todas como leídas
                        </button>
                    </div>
                    )}
                </div>
                </div>
            </div>,
            document.body
            )
        : null}
    </div>
  )
}